import { ArrowBack } from '@mui/icons-material';
import { Box, styled } from '@mui/material';
import { Environment, OrbitControls } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import React, {
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useConfigurator } from 'src/hook/useConfigurator';
import { useConfiguratorNavigation } from 'src/hook/useConfiguratorNavigation';
import useLoadData from 'src/hook/useLoadData';
import { Assembly, getAssembly } from 'src/services/assemblies';
import { compare2Float32Array } from 'src/utils/configurator.utils';
import * as THREE from 'three';
import { WebGLRenderer } from 'three';

import { TexturesButtons } from 'src/components/TextureButtons';
import { PriceRecap } from 'src/components/PriceRecap';
import { StockRecap } from 'src/components/StockRecap';
import { Modal } from '../components/3d/Fragments/Modal';
import { Interface } from '../components/3d/Interface/Index';
import { LowerCaseButton } from '../components/common';
import AdminNavBar from './admin/components/AdminNavBar';
import NavBar, { QuoteStep } from './project/components/NavBar';
import Recap from './project/Recap';
import { Monument } from './Monument';
import { useConfiguratorState } from './useConfiguratorState';
import { useTakePictures } from './useTakePictures';

// --- Types exportés (utilisés par 30+ fichiers) ---

type AnglesProps = {
  piece: any;
  selectedPoints: { pieceIndex: number; point: Float32Array }[] | null;
  pieceIndex: number;
  selectPoints: (pts: Float32Array) => void;
};

export type Options = { backgroundColor: string; light: number };

export type States = {
  options: Options;
  texture: boolean;
  points: boolean;
  allPieces: boolean;
  hasAdvancedMode: boolean;
  wired: boolean;
  advancedMove: boolean;
  modelId: number;
};

export type StateNames = keyof States;

export type ModalType =
  | 'additionalServices'
  | 'editInformations'
  | 'quoteImage'
  | 'installations'
  | 'addComment';

// --- Composant Angles (exporté, utilisé par Piece.tsx) ---

export const Angles = ({
  piece,
  selectedPoints,
  selectPoints,
  pieceIndex,
}: AnglesProps) => {
  const [points, setPoints] = useState<Float32Array[] | null>([]);

  useEffect(() => {
    const pointsTmp: Float32Array[] | null = [];
    const pts = piece.geometry.attributes.position.array;

    for (let i = 0; i < pts.length; i += 3) {
      pointsTmp.push(new Float32Array([pts[i], pts[i + 1], pts[i + 2]]));
    }
    setPoints(pointsTmp);
  }, [piece]);

  return (
    <>
      {points?.map((point, index) => {
        const isSelected = selectedPoints
          ? !!selectedPoints.find((el) => {
              return (
                compare2Float32Array(el.point, point) &&
                pieceIndex === el.pieceIndex
              );
            })
          : false;

        const colors = isSelected ? new Float32Array([1, 0, 0, 1]) : null;

        return (
          <points
            onClick={(e) => {
              if (e.distanceToRay && e.distanceToRay < 0.002)
                selectPoints(point);
            }}
            key={index}
            position={piece.position}
          >
            <bufferGeometry attach='geometry'>
              <bufferAttribute
                attach='attributes-position'
                count={point.length / 3}
                array={point}
                itemSize={3}
                usage={THREE.DynamicDrawUsage}
                needsUpdate={isSelected}
              />
              {colors ? (
                <bufferAttribute
                  attach='attributes-color'
                  count={colors.length / 3}
                  array={colors}
                  itemSize={3}
                  usage={THREE.DynamicDrawUsage}
                />
              ) : null}
            </bufferGeometry>
            <pointsMaterial
              attach='material'
              vertexColors
              size={8}
              sizeAttenuation={false}
              fog={false}
            />
          </points>
        );
      })}
    </>
  );
};

// --- SceneWithClipping ---

const SceneWithClipping = ({ children }: { children: any }) => {
  const onCreated = React.useCallback((state: any) => {
    state.gl.localClippingEnabled = true;
  }, []);

  return (
    <Canvas
      id='canvas'
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: 'white',
      }}
      camera={{
        position: [1.5, 1, 3.5],
        fov: 40,
        near: 0.1,
        far: 1000,
      }}
      dpr={window.devicePixelRatio}
      gl={{
        preserveDrawingBuffer: true,
        localClippingEnabled: true,
        antialias: true,
        alpha: true,
        powerPreference: 'high-performance',
      }}
      raycaster={{
        params: {
          Points: { threshold: 0.01 },
          Line: { threshold: 0.01 },
        },
      }}
      shadows
      onCreated={onCreated}
    >
      {children}
    </Canvas>
  );
};

// --- Composant principal ---

type ConfiguratorInitProps = {
  assembly: Assembly | null;
  onRefresh: () => void;
  assemblyId: string | undefined;
};

const ConfiguratorInit = ({
  assembly,
  onRefresh,
  assemblyId,
}: ConfiguratorInitProps) => {
  const [openedModal, setOpenedModal] = useState<ModalType>();
  const orbitRef = useRef<any>();
  const [gl, setGl] = useState<WebGLRenderer>();

  const {
    selectedPoints,
    constraints,
    userModifications,
    history,
    pieces,
    pieceIndex,
    reloadPiece,
    geometry,
    activePieces,
    customSteles,
    customPlatings,
    granits,
    customSoles,
    customBoxes,
    setSelectedPoints,
    changePoint,
    refreshConstraints,
    refreshModifications,
    setPieceIndex,
    setReloadPiece,
    changeGeometry,
    setActivePieces,
    changePosition,
    setCustomSteles,
    setCustomSoles,
    setCustomBoxes,
    removeOnePiece,
    movePieceOnAxe,
    checkCollision,
    setCustomPlatings,
    findPointOnTheSameAxe,
    findPointAround,
    getAssemblyDetails,
    patterns,
    setPatterns,
    getAssemblySize,
    centerCamera,
  } = useConfigurator(assembly, orbitRef, gl);

  const { setValue, getValue, hasAdvancedMove } = useConfiguratorState(
    assembly?.modelId ?? -1,
  );

  const [selectedPattern, setSelectedPattern] = useState<number>();
  const [orientation, setOrientation] = useState<{
    key: 'x' | 'y' | 'z' | 'free';
    inverse: boolean;
  }>({ key: 'free', inverse: false });

  const { takePictures } = useTakePictures(
    gl,
    orbitRef,
    pieces,
    customSteles,
    customPlatings,
    customSoles,
    patterns,
    centerCamera,
    setValue,
    setPieceIndex,
  );

  const {
    handleNextStep,
    handlePrevStep,
    submit,
    modalComponent,
    step,
    selectedPage,
    setSelectedPage,
    setStep,
    isLoading,
    activeInstallations,
    setActiveInstallations,
    handleSave,
  } = useConfiguratorNavigation(
    pieces,
    customSteles,
    activePieces,
    customPlatings,
    customSoles,
    customBoxes,
    patterns,
    assemblyId,
    gl,
    assembly,
    onRefresh,
    setOpenedModal,
    getAssemblyDetails,
    orbitRef,
    takePictures,
  );

  useEffect(() => {
    if (!hasAdvancedMove) centerCamera();
  }, [hasAdvancedMove, centerCamera]);

  return (
    <Container>
      {assembly?.quote ? (
        <NavBar currStep={step} setStep={setStep} handleSave={handleSave} />
      ) : (
        <AdminNavBar />
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          position: 'relative',
          height: window.innerHeight - 71,
          overflow: 'hidden',
        }}
      >
        <TexturesButtons step={step} setValue={setValue} getValue={getValue} />
        <StockRecap step={step} pieces={pieces} history={history} />
        {assembly && step !== 'QUOTE' ? (
          <PriceRecap
            assembly={assembly}
            setModal={setOpenedModal}
            submit={submit}
            getAssemblyDetails={getAssemblyDetails}
            getAssemblySize={getAssemblySize}
            isLoading={isLoading}
            activePieces={activePieces}
            platings={customPlatings}
            steles={customSteles}
            boxes={customBoxes}
            activeInstallations={activeInstallations}
            setActiveInstallations={setActiveInstallations}
            soles={customSoles}
          />
        ) : null}
        <CanvasWrapper
          step={step}
          style={isLoading && step === 'QUOTE' ? { width: '100vw' } : {}}
        >
          <Modal
            isOpen={!!openedModal}
            onClose={() => {
              setOpenedModal(undefined);
            }}
          >
            {openedModal && modalComponent(openedModal)}
          </Modal>
          {step === 'QUOTE' && (
            <>
              {isLoading ? (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#ffffff',
                    opacity: 0.8,
                    zIndex: 999,
                  }}
                >
                  <p style={{ opacity: 1 }}>
                    Le configurateur génère les rendus.
                  </p>
                  <p style={{ opacity: 1 }}>Veuillez patienter</p>
                </div>
              ) : (
                <StyledButton onClick={handlePrevStep}>
                  <ArrowBack />
                  Etape précédente
                </StyledButton>
              )}
            </>
          )}

          <SceneWithClipping>
            {step === 'QUOTE' ? (
              <color attach='background' args={['#ffffff']} />
            ) : null}
            <directionalLight position={[2.5, 2, 2]} intensity={0.5} />
            <directionalLight position={[-2.5, 2, 2]} intensity={0.5} />
            <directionalLight position={[0, 2, 2]} intensity={0.5} />

            <Environment files='/buildings/forest_slope_4k_15.hdr' />

            <Suspense fallback={null}>
              <Monument
                setGl={setGl}
                selectedPoints={selectedPoints}
                setSelectedPoints={setSelectedPoints}
                pieces={pieces}
                pieceIndex={pieceIndex}
                setPieceIndex={setPieceIndex}
                reloadPiece={reloadPiece}
                setReloadPiece={setReloadPiece}
                changePosition={changePosition}
                customSteles={customSteles}
                setCustomSteles={setCustomSteles}
                customPlatings={customPlatings}
                setCustomPlatings={setCustomPlatings}
                setValue={setValue}
                getValue={getValue}
                orbitRef={orbitRef}
                activePieces={activePieces}
                checkCollision={checkCollision}
                step={step}
                selectedPattern={selectedPattern}
                patterns={patterns}
                orientation={orientation}
                centerCamera={centerCamera}
                setCustomSoles={setCustomSoles}
                customSoles={customSoles}
                setCustomBoxes={setCustomBoxes}
                customBoxes={customBoxes}
              />
            </Suspense>
          </SceneWithClipping>
        </CanvasWrapper>
        {isLoading && step === 'QUOTE' ? null : (
          <InterfaceWrapper step={step}>
            {step === 'QUOTE' && assembly ? (
              <Recap
                assembly={assembly}
                setModal={setOpenedModal}
                submit={submit}
                getAssemblyDetails={getAssemblyDetails}
                getAssemblySize={getAssemblySize}
                isLoading={isLoading}
                activePieces={activePieces}
                platings={customPlatings}
                steles={customSteles}
                boxes={customBoxes}
                activeInstallations={activeInstallations}
                setActiveInstallations={setActiveInstallations}
                soles={customSoles}
              />
            ) : (
              <Interface
                gl={gl}
                selectedPoints={selectedPoints}
                setSelectedPoints={setSelectedPoints}
                constraints={constraints}
                changePoint={changePoint}
                modifications={userModifications}
                history={history}
                refreshConstraints={refreshConstraints}
                refreshModifications={refreshModifications}
                pieceIndex={pieceIndex}
                setPieceIndex={setPieceIndex}
                changeGeometry={changeGeometry}
                geometry={geometry}
                setActivePieces={setActivePieces}
                pieces={pieces}
                activePieces={activePieces}
                customSteles={customSteles}
                setCustomSteles={setCustomSteles}
                checkCollision={checkCollision}
                assembly={assembly}
                setCustomPlatings={setCustomPlatings}
                customPlatings={customPlatings}
                removeOnePiece={removeOnePiece}
                setValue={setValue}
                getValue={getValue}
                findPointOnTheSameAxe={findPointOnTheSameAxe}
                findPointAround={findPointAround}
                movePieceOnAxe={movePieceOnAxe}
                orbitRef={orbitRef}
                handleNextStep={handleNextStep}
                handlePrevStep={handlePrevStep}
                step={step}
                reloadPiece={reloadPiece}
                quote={!!assembly?.quote}
                granits={granits}
                setReloadPiece={setReloadPiece}
                centerCamera={centerCamera}
                setPatterns={setPatterns}
                patterns={patterns}
                selectedPattern={selectedPattern}
                setSelectedPattern={setSelectedPattern}
                orientation={orientation}
                setOrientation={setOrientation}
                selectedPage={selectedPage}
                setSelectedPage={setSelectedPage}
                isLoading={isLoading}
                setCustomSoles={setCustomSoles}
                customSoles={customSoles}
                setCustomBoxes={setCustomBoxes}
                customBoxes={customBoxes}
              />
            )}
          </InterfaceWrapper>
        )}
      </div>
    </Container>
  );
};

// --- Composant d'entrée ---

const Configurator = () => {
  const params = useParams<{ assemblyId: string }>();

  const { data: assembly, onRefresh } = useLoadData(() =>
    getAssembly(params.assemblyId ?? undefined),
  );

  if (params.assemblyId !== '-1' && assembly === null) return <></>;

  return (
    <ConfiguratorInit
      assembly={assembly}
      onRefresh={onRefresh}
      assemblyId={params.assemblyId}
    />
  );
};

// --- Styled components ---

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
});

const CanvasWrapper = styled(Box)(({ step }: { step: QuoteStep }) => ({
  height: '100%',
  width: step === 'QUOTE' ? '55vw' : '65vw',
  position: 'relative',
}));

const InterfaceWrapper = styled(Box)(({ step }: { step: QuoteStep }) => ({
  display: 'flex',
  position: 'relative',
  width: step === 'QUOTE' ? '45vw' : '35vw',
  boxShadow: ' -4px 0px 15px rgba(0, 0, 0, 0.1)',
  backgroundColor: '#FFFFFF',
  height: '100%',
}));

const StyledButton = styled(LowerCaseButton)({
  position: 'absolute',
  left: 50,
  top: 20,
  zIndex: 2,
  background: 'white',
  color: 'black',
  display: 'flex',
  gap: '16px',
});

export default Configurator;
